import { Component, OnInit } from '@angular/core';
import { FormControl, Validators, FormBuilder, FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import {
  DeleteToken,
  ELoginActions,
  GetToken,
  GetTokenFailure,
  GetTokenSuccess
} from '../../store/actions/login.actions';
import { ConstsHelper } from '../../../../helpers/consts.helper';
import { GetCustomer } from '../../../account/store/actions/customer.actions';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '../../../../../environments/environment';
import { CustomerStorageService } from '../../../account/services/customer-storage.service';
import { TogglePasswordService } from '../../services/toggle-password.service';
import { TokenLocalStorageService } from '../../services/token-local-storage.service';
import { LoginRequestInterface } from '../../models/login-request.interface';
import { Actions, ofType } from '@ngrx/effects';
import { MfaService } from '../../../shared/services/mfa.service';
import { GetAlertMessage } from '../../../shared/store/actions/alert-message.actions';
import { AlertMessageInterface } from '../../../shared/models/alert-message.interface';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
  standalone: false
})
export class LoginComponent implements OnInit {

  loginForm: FormGroup;
  control: FormControl;
  loginFormSubmitted: boolean;
  environment = environment;
  captchaToken: string;
  captchaKey: string;
  displayConfirmationCode: boolean;
  confirmationCode: string|null;
  customerId: string|null;
  errorMessageConfirmationCode: string|null;
  errorMessageLogin: string|null;
  brand?: string;
  vitrineUrl?: string;
  sendMfaCodeBy: string;

  errors: any = {
    login: {
      required: 'Le numéro de téléphone ou l\'adresse email est obligatoire.'
    },
    password: {
      required: `Le mot de passe est obligatoire.`,
      pattern: `Votre mot de passe est invalide.`
    }
  };

  breadcrumbDetails: any = [
    {
      url: environment.data.vitrineUrl,
      label: `Accueil`
    },
    {
      url: null,
      label: `Identification`
    }
  ];

  constructor(
      private router: Router,
      private action$: Actions,
      private activateRoute: ActivatedRoute,
      private formBuilder: FormBuilder,
      private store: Store<any>,
      private customerStorageService: CustomerStorageService,
      public togglePasswordService: TogglePasswordService,
      private localStorageService: TokenLocalStorageService,
      private mfaService: MfaService,
  ) {}

  ngOnInit() {
    this.sendMfaCodeBy = 'email';
    this.displayConfirmationCode = false;
    this.confirmationCode = null;
    this.errorMessageConfirmationCode = null;
    this.errorMessageLogin = null;
    this.customerId = null;
    this.brand = environment.data.brand;
    this.vitrineUrl = environment.data.vitrineUrl;
    if (this.localStorageService.getToken()) {
      this.store.dispatch(new GetCustomer());
      this.router.navigate(['/lignes']);
    } else if (this.customerStorageService.getCustomer() !== null) {
      window.top.location.href = this.environment.data.url;
      history.forward();
    } else {
      this.loginFormSubmitted = false;
      this.store.dispatch(new DeleteToken());
      this.captchaKey = null;
      this.captchaToken = null;
      this.captchaKey = environment.data.captchaKey;
      this.initForms();
    }
    this.action$.pipe(ofType(ELoginActions.GetTokenSuccess)).subscribe((result: GetTokenSuccess) => {
      if ('customerId' in result.payload) {
        this.displayConfirmationCode = true;
        this.loginFormSubmitted = false;
        this.customerId = result.payload.customerId;
      }
    });
    this.action$.pipe(ofType(ELoginActions.GetTokenFailure)).subscribe((result: GetTokenFailure) => {
        this.loginFormSubmitted = false;
    });
  }

  validateCode() {
    this.errorMessageConfirmationCode = null;
    if ([null, ''].indexOf(this.confirmationCode) !== -1) {
      this.errorMessageConfirmationCode = 'Ce champ est obligatoire.';
    } else {
      this.mfaService.checkLoginCode(this.loginForm.get('login').value, 'authentication', this.confirmationCode, this.customerId).subscribe(
          response => {
            this.store.dispatch(new GetTokenSuccess(response));
            this.localStorageService.save(response, true);
            if (response.isAdminProFleet) {
              localStorage.setItem(ConstsHelper.LOCAL_STORAGE_ADMIN_PRO_FEET_TOKEN, response.token);
            }
            this.store.dispatch(new GetCustomer());
          }, error => {
            const alertMessage = {message: ConstsHelper.ERROR_OCCURRED_RETRY_MESSAGE, code: 400} as AlertMessageInterface;
            if (error.error.error_message && error.error.error_message.includes('invalid_code')) {
              alertMessage.message = ConstsHelper.ERROR_MESSAGE_MFA_LOGIN_INVALID_CODE;
            } else if (error.error.error_message && error.error.error_message.includes('blocked')) {
              alertMessage.message = ConstsHelper.ERROR_MESSAGE_MFA_BLOCKED;
              this.cancel();
            }
            this.store.dispatch(new GetAlertMessage(alertMessage));
          }
      );
    }
  }

  initForms() {
    this.control = this.formBuilder.control('', Validators.required);
    this.loginForm = this.formBuilder.group({});
    this.loginForm.addControl('login', this.formBuilder.control(this.activateRoute.snapshot.params['msisdn'] ? this.activateRoute.snapshot.params['msisdn'] : '', [Validators.required]));
    this.loginForm.addControl('password', this.formBuilder.control('', [Validators.required]));
  }

  signIn() {
    this.errorMessageLogin = null;
    this.loginFormSubmitted = true;
    if (this.loginForm.valid) {
      if (
          ConstsHelper.emailPattern.test(this.loginForm.get('login').value) ||
          ConstsHelper.phoneNumberPattern.test(this.loginForm.get('login').value)
      ) {
        this.sendMfaCodeBy = ConstsHelper.emailPattern.test(this.loginForm.get('login').value) ? 'email' : 'sms';
        const loginRequestInterface = {
          email: ConstsHelper.emailPattern.test(this.loginForm.get('login').value) ? this.loginForm.get('login').value.replace(/ /g, '') : null,
          msisdn: ConstsHelper.phoneNumberPattern.test(this.loginForm.get('login').value) ? this.loginForm.get('login').value : null,
          password: this.loginForm.get('password').value,
          captchaKey: this.captchaKey,
          captchaToken: this.captchaToken
        } as LoginRequestInterface;
        this.store.dispatch(new GetToken(loginRequestInterface));
      } else {
        this.errorMessageLogin = 'Le numéro de téléphone ou l\'adresse email n\'est pas valide.';
      }
    }
  }

  resolved(captchaResponse: string) {
    this.captchaToken = captchaResponse;
    if (this.captchaToken && !this.loginFormSubmitted) {
      this.signIn();
    }
  }

  onLoginClick(captchaRef: any): void {
    captchaRef.reset();
    captchaRef.execute();
  }

  errored(captchaRef: any) {
    captchaRef.reset();
    captchaRef.execute();
  }

  goTo(page: string) {
    this.router.navigate(['/' + page]);
  }

  getError(formControlValues: string): string {
    let errorMsg = '';
    if (this.loginForm.controls[formControlValues].invalid) {
      Object.keys(this.loginForm.controls[formControlValues].errors).map(
          key => {
            errorMsg = this.errors[formControlValues][key];
          }
      );
    }
    return errorMsg;
  }

  cancel() {
    this.errorMessageConfirmationCode = null;
    this.customerId = null;
    this.confirmationCode = null;
    this.displayConfirmationCode = false;
  }
}
